import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Divider, Grid,
  MenuItem, Paper, TextField, Typography, IconButton,
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import heic2any from 'heic2any';
import api from '../api';

const SEX_OPTIONS = ['Male', 'Female', 'Heifer', 'Steer'];

const EMPTY_FORM = {
  cow_number: '', dob: '', sex: 'Heifer', breed: '', owner: '',
  dam: null, sire: null,
  bought_date: '', purchase_price: '',
  weaned_date: '',
  sold_date: '', sold_amount: '',
  death_date: '',
  notes: '',
};

export default function CowForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(EMPTY_FORM);
  const [farmers, setFarmers] = useState([]);
  const [allCows, setAllCows] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [imageFiles, setImageFiles] = useState([]);
  const [imageConverting, setImageConverting] = useState(false);
  const fileInputRef = useRef(null);

  const handleImageSelect = async (e) => {
    const rawFiles = Array.from(e.target.files);
    if (!rawFiles.length) return;
    setImageConverting(true);
    const converted = [];
    for (let file of rawFiles) {
      const isHeic = /\.(heic|heif)$/i.test(file.name) || file.type === 'image/heic' || file.type === 'image/heif';
      if (isHeic) {
        try {
          const blob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.85 });
          file = new File([blob], file.name.replace(/\.heic$/i, '.jpg'), { type: 'image/jpeg' });
        } catch {
          setError('Could not convert one or more HEIC images. Try saving as JPEG first.');
          setImageConverting(false);
          return;
        }
      }
      converted.push(file);
    }
    setImageFiles((prev) => [...prev, ...converted]);
    setImageConverting(false);
    e.target.value = '';
  };

  const fetchData = useCallback(async () => {
    const [farmersRes, cowsRes] = await Promise.all([
      api.get('/farmers/'),
      api.get('/cows/'),
    ]);
    setFarmers(farmersRes.data.filter((f) => !f.is_farm === false ? true : !f.is_farm));
    setAllCows(cowsRes.data);

    if (isEdit) {
      const cowRes = await api.get(`/cows/${id}/`);
      const c = cowRes.data;
      setForm({
        cow_number: c.cow_number ?? '',
        dob: c.dob ?? '',
        sex: c.sex ?? 'Heifer',
        breed: c.breed ?? '',
        owner: c.owner ?? '',
        dam: c.dam ?? null,
        sire: c.sire ?? null,
        bought_date: c.bought_date ?? '',
        purchase_price: c.purchase_price ?? '',
        weaned_date: c.weaned_date ?? '',
        sold_date: c.sold_date ?? '',
        sold_amount: c.sold_amount ?? '',
        death_date: c.death_date ?? '',
        notes: c.notes ?? '',
      });
    }
  }, [id, isEdit]);

  useEffect(() => {
    fetchData().finally(() => setLoading(false));
  }, [fetchData]);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    // Build payload — strip empty optional strings to null
    const payload = { ...form };
    ['bought_date', 'purchase_price', 'weaned_date', 'sold_date', 'sold_amount', 'death_date'].forEach((f) => {
      if (payload[f] === '') payload[f] = null;
    });

    try {
      let cowId = id;
      if (isEdit) {
        await api.patch(`/cows/${id}/`, payload);
      } else {
        const res = await api.post('/cows/', payload);
        cowId = res.data.id;
      }
      for (let i = 0; i < imageFiles.length; i++) {
        try {
          const fd = new FormData();
          fd.append('cow', cowId);
          fd.append('image', imageFiles[i]);
          fd.append('is_main', String(i === 0));
          await api.post('/cow-images/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        } catch {
          // non-fatal — cow saved, image(s) may partially fail
        }
      }
      navigate(isEdit ? `/herd/${id}` : '/herd');
    } catch (err) {
      const data = err.response?.data;
      setError(data ? Object.entries(data).map(([k, v]) => `${k}: ${v}`).join(' | ') : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;
  }

  const cowOptions = allCows.filter((c) => !isEdit || c.id !== Number(id));

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 800, mx: 'auto' }}>
      <Box display="flex" alignItems="center" gap={1} mb={3}>
        <IconButton onClick={() => navigate(isEdit ? `/herd/${id}` : '/herd')} aria-label="Go back">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight={700}>{isEdit ? 'Edit Cow' : 'Add Cow'}</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box component="form" onSubmit={handleSubmit}>
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" mb={2}>Identity</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField label="Tag / Cow Number" value={form.cow_number} onChange={set('cow_number')} required fullWidth />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Date of Birth" type="date" value={form.dob} onChange={set('dob')} required fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select label="Sex" value={form.sex} onChange={set('sex')} required fullWidth>
                {SEX_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Breed" value={form.breed} onChange={set('breed')} fullWidth />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Owner" value={form.owner} onChange={set('owner')} required fullWidth>
                {farmers.map((f) => (
                  <MenuItem key={f.id} value={f.id}>{f.username}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" mb={2}>Genealogy</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={cowOptions.filter((c) => ['Female', 'Heifer'].includes(c.sex))}
                getOptionLabel={(o) => o.cow_number ?? String(o)}
                value={cowOptions.find((c) => c.id === form.dam) ?? null}
                onChange={(_, v) => setForm({ ...form, dam: v?.id ?? null })}
                renderInput={(params) => <TextField {...params} label="Dam (Mother)" fullWidth />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={cowOptions.filter((c) => c.sex === 'Male')}
                getOptionLabel={(o) => o.cow_number ?? String(o)}
                value={cowOptions.find((c) => c.id === form.sire) ?? null}
                onChange={(_, v) => setForm({ ...form, sire: v?.id ?? null })}
                renderInput={(params) => <TextField {...params} label="Sire (Father)" fullWidth />}
              />
            </Grid>
          </Grid>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" mb={2}>Acquisition</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField label="Bought Date" type="date" value={form.bought_date} onChange={set('bought_date')} fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Purchase Price" type="number" value={form.purchase_price} onChange={set('purchase_price')} fullWidth inputProps={{ step: '0.01' }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Weaned Date" type="date" value={form.weaned_date} onChange={set('weaned_date')} fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
          </Grid>
        </Paper>

        {isEdit && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" mb={2}>Disposition</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}><Divider textAlign="left"><Typography variant="caption">Sold</Typography></Divider></Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Sold Date" type="date" value={form.sold_date} onChange={set('sold_date')} fullWidth InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Sold Amount" type="number" value={form.sold_amount} onChange={set('sold_amount')} fullWidth inputProps={{ step: '0.01' }} />
              </Grid>
              <Grid item xs={12}><Divider sx={{ mt: 0.5 }} /></Grid>
              <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 58 }}>Deceased</Typography>
                <TextField label="Death Date" type="date" value={form.death_date} onChange={set('death_date')} fullWidth InputLabelProps={{ shrink: true }} />
              </Grid>
            </Grid>
          </Paper>
        )}

        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" mb={2}>Notes</Typography>
          <TextField value={form.notes} onChange={set('notes')} fullWidth multiline rows={3} placeholder="Markings, history, tags in wrong ear, etc." />
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" mb={2}>Photo</Typography>
          <input
            type="file"
            accept="image/jpeg,image/png,image/heic,image/heif,.heic,.heif"
            style={{ display: 'none' }}
            ref={fileInputRef}
            onChange={handleImageSelect}
            multiple
          />
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={imageConverting ? <CircularProgress size={16} /> : <AddPhotoAlternateIcon />}
              onClick={() => fileInputRef.current.click()}
              disabled={imageConverting}
            >
              {imageConverting ? 'Converting…' : 'Add Photos'}
            </Button>
            {imageFiles.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                {imageFiles.length} photo{imageFiles.length !== 1 ? 's' : ''} selected
              </Typography>
            )}
          </Box>
          {imageFiles.length > 0 && (
            <Box mt={1} display="flex" flexDirection="column" gap={0.5}>
              {imageFiles.map((f, i) => (
                <Box key={i} display="flex" alignItems="center" gap={1}>
                  <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                    {f.name}{i === 0 ? ' (main)' : ''}
                  </Typography>
                  <Button size="small" color="error" sx={{ minWidth: 0, p: 0.25 }}
                    aria-label={`Remove ${f.name}`}
                    onClick={() => setImageFiles((prev) => prev.filter((_, idx) => idx !== i))}>
                    ✕
                  </Button>
                </Box>
              ))}
            </Box>
          )}
        </Paper>

        <Box display="flex" gap={2} justifyContent="flex-end">
          <Button onClick={() => navigate(isEdit ? `/herd/${id}` : '/herd')}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Cow'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
